(() => {
  const money = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  });

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  function getPackSize(variant, isCombo) {
    if (isCombo) return 2;

    const optionValues = Array.isArray(variant?.options) ? variant.options : [];
    for (let index = optionValues.length - 1; index >= 0; index -= 1) {
      const match = String(optionValues[index]).match(/(?:^|\D)(10|5|2|1)(?:\D|$)/);
      if (match) return Number(match[1]);
    }

    const titleMatch = String(variant?.title || '').match(/(?:^|\D)(10|5|2|1)(?:\D|$)/);
    return titleMatch ? Number(titleMatch[1]) : 1;
  }

  function packName(packSize, isCombo) {
    if (isCombo) return '2-book devotional combo';
    return `Pack ${packSize}`;
  }

  function packHint(packSize) {
    const labels = {
      1: 'Personal copy',
      2: 'Keep one, gift one',
      5: 'Family & gifting',
      10: 'Bulk gifting'
    };
    return labels[packSize] || 'Choose this pack';
  }

  function findProductDetails(main) {
    return (
      main.querySelector('.product-details') ||
      main.querySelector('[data-testid="product-information"] [class*="product-details"]')
    );
  }

  function findBuyArea(details) {
    const form = details?.querySelector('form[action*="/cart/add"]');
    if (!form) return null;
    return (
      form.closest('buy-buttons-block, .buy-buttons-block, [class*="buy-buttons"]') ||
      form
    );
  }

  function selectedVariantId(main) {
    const selectedRadio = Array.from(
      main.querySelectorAll('variant-picker input[type="radio"]:checked[data-variant-id]')
    ).reverse().find((input) => input.dataset.variantId);

    if (selectedRadio?.dataset.variantId) return String(selectedRadio.dataset.variantId);

    const productFormId = main.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
    return productFormId ? String(productFormId) : '';
  }

  function annotatePackChoices(main) {
    main.querySelectorAll('.variant-option__button-label').forEach((label) => {
      if (label.dataset.phase2Annotated === 'true') return;

      const input = label.querySelector('input');
      const value = input?.value || label.textContent;
      const match = String(value).trim().match(/^(10|5|2|1)$/);
      if (!match) return;

      const text = label.querySelector('[data-key="variant-option-text"]');
      if (!text) return;

      const hint = document.createElement('small');
      hint.className = 'phase2-pack-hint';
      hint.textContent = packHint(Number(match[1]));
      text.appendChild(hint);
      label.dataset.phase2Annotated = 'true';
    });
  }

  function initProduct(root = document) {
    const main =
      root.querySelector?.('main[data-template^="product"]') ||
      document.querySelector('main[data-template^="product"]');
    const source =
      root.querySelector?.('[data-phase2-pdp-root]') ||
      document.querySelector('[data-phase2-pdp-root]');

    if (!main || !source || source.dataset.phase2Ready === 'true') return;

    const details = findProductDetails(main);
    const valuePanel = source.querySelector('[data-phase2-pdp-value]');
    const confidence = source.querySelector('[data-phase2-pdp-confidence]');
    const variantScript = source.querySelector('[data-phase2-variant-json]');
    if (!details || !valuePanel || !confidence || !variantScript) return;

    let variants = [];
    try {
      variants = JSON.parse(variantScript.textContent);
    } catch (_) {
      variants = [];
    }

    const isCombo = source.dataset.productCombo === 'true';
    const story = document.querySelector('.phase2-product-story');
    const firstProductSection = main.querySelector(':scope > .shopify-section');
    if (story && firstProductSection) {
      firstProductSection.insertAdjacentElement('afterend', story);
    }

    const picker = details.querySelector('variant-picker');
    const buyArea = findBuyArea(details);

    if (picker) {
      picker.insertAdjacentElement('afterend', valuePanel);
    } else if (buyArea) {
      buyArea.insertAdjacentElement('beforebegin', valuePanel);
    } else {
      details.appendChild(valuePanel);
    }

    if (buyArea) {
      buyArea.insertAdjacentElement('afterend', confidence);
    } else {
      details.appendChild(confidence);
    }

    const updateContent = () => {
      annotatePackChoices(main);

      const id = selectedVariantId(main);
      const checkedVariant = variants.find((variant) => String(variant.id) === id);
      const variant = checkedVariant || variants.find((item) => item.available) || variants[0];
      if (!variant) return;

      const price = toNumber(variant.price);
      const compareAt = toNumber(variant.compare_at_price);
      const saving = Math.max(compareAt - price, 0);
      const packSize = getPackSize(variant, isCombo);
      const perBook = packSize > 0 ? Math.round(price / packSize) : price;

      valuePanel.querySelector('[data-phase2-selection]').textContent = packName(packSize, isCombo);
      valuePanel.querySelector('[data-phase2-price]').textContent = money.format(price / 100);
      valuePanel.querySelector('[data-phase2-per-book]').textContent =
        `${money.format(perBook / 100)} / book`;

      const mrpWrap = valuePanel.querySelector('[data-phase2-mrp-wrap]');
      const mrp = valuePanel.querySelector('[data-phase2-mrp]');
      const savingNode = valuePanel.querySelector('[data-phase2-saving]');

      if (compareAt > price) {
        mrpWrap.hidden = false;
        mrp.textContent = money.format(compareAt / 100);
        savingNode.textContent = `Save ${money.format(saving / 100)}`;
      } else {
        mrpWrap.hidden = true;
        savingNode.textContent = '';
      }

      const note = valuePanel.querySelector('[data-phase2-value-note]');
      note.textContent = isCombo
        ? 'Includes two devotional editions in one meaningful combo.'
        : packSize === 1
          ? 'A personal copy for daily devotion, home or travel.'
          : `${packSize} books selected—ideal for family use and meaningful gifting.`;

      const submit = main.querySelector('form[action*="/cart/add"] button[type="submit"]');
      if (submit && !submit.disabled) {
        const text = submit.querySelector('.add-to-cart-text__content span span') ||
          submit.querySelector('.add-to-cart-text__content') ||
          submit;
        if (text) text.textContent = 'Add to Cart';
      }

      const stickyTitle = document.querySelector('.phase0-sticky-cart__title');
      if (stickyTitle) {
        stickyTitle.textContent = `${packName(packSize, isCombo)} selected`;
      }

      const stickyPrice = document.querySelector('[data-phase0-sticky-price]');
      if (stickyPrice) {
        stickyPrice.textContent = saving > 0
          ? `${money.format(price / 100)} · Save ${money.format(saving / 100)}`
          : money.format(price / 100);
      }
    };

    const observerOptions = {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['checked', 'disabled', 'aria-disabled', 'value']
    };
    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => { queued = false; update(); });
    });
    const update = () => {
      observer.disconnect();
      try { updateContent(); }
      finally { observer.observe(details, observerOptions); }
    };
    main.addEventListener('change', () => window.setTimeout(update, 80));

    source.dataset.phase2Ready = 'true';
    update();
  }

  function applyPhaseTwo(root = document) {
    initProduct(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyPhaseTwo());
  } else {
    applyPhaseTwo();
  }

  document.addEventListener('shopify:section:load', (event) => applyPhaseTwo(event.target));
})();
